/**
 * PesataModal (#91/#94) — transizione in_transito → attesa_compliance.
 * Seconda pesata (tara) + peso netto: calcolato lordo−tara (mostrato), oppure
 * inserito manualmente (#94, fa fede). → attesa-compliance.
 */
import { useState } from 'react';
import { Scale, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { attesaCompliance } from '../../api/conferimenti';
import type { ConferimentoDetail } from '../../api/conferimenti';
import { fmtKg } from '../../lib/saldo';

const schema = z.object({
  tara_kg: z.preprocess((v) => (v === '' || v == null ? undefined : Number(v)), z.number().nonnegative().optional()),
  peso_netto_kg: z.preprocess((v) => (v === '' || v == null ? undefined : Number(v)), z.number().nonnegative().optional()),
});
type FormData = z.infer<typeof schema>;

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

interface Props {
  conferimentoId: string;
  numero: string | null;
  pesoLordoKg: number | null;
  onClose: () => void;
  onSaved: (updated: ConferimentoDetail) => void;
}

export default function PesataModal({ conferimentoId, numero, pesoLordoKg, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<string | null>(null);
  const { register, handleSubmit, watch, formState: { isSubmitting } } = useForm({
    resolver: zodResolver(schema),
  });
  // eslint-disable-next-line react-hooks/incompatible-library -- RHF watch(): il progetto non usa il React Compiler
  const tara = watch('tara_kg');
  const nettoManuale = watch('peso_netto_kg');
  const nettoCalcolato =
    pesoLordoKg != null && tara != null && !Number.isNaN(Number(tara))
      ? pesoLordoKg - Number(tara)
      : null;

  async function onSubmit(data: FormData) {
    setServerError(null);
    try {
      const updated = await attesaCompliance(conferimentoId, {
        tara_kg: data.tara_kg,
        peso_netto_kg: data.peso_netto_kg,
      });
      onSaved(updated);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setServerError(typeof detail === 'string' ? detail : t('conferimenti.pesata_modal.errore_salvataggio'));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <Scale size={16} className="text-sage" /> {t('conferimenti.pesata_modal.titolo')}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit((d) => onSubmit(d as FormData))}>
          <div className="px-6 py-5 space-y-4">
            <p className="text-xs text-ink-soft">
              <Trans i18nKey="conferimenti.pesata_modal.riepilogo"
                values={{ numero: numero ?? '—', lordo: pesoLordoKg != null ? `${fmtKg(pesoLordoKg)} kg` : '—' }}
                components={{ strong: <strong /> }} />
            </p>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.pesata_modal.tara')}</label>
              <input type="number" step="0.01" className={inputCls} placeholder="0" {...register('tara_kg')} />
            </div>
            {nettoManuale == null && nettoCalcolato != null && (
              <div className="px-3 py-2 bg-sage-light/40 border border-[#c5d0c2] rounded text-sm text-ink-dark">
                <Trans i18nKey="conferimenti.pesata_modal.netto_calcolato"
                  values={{ n: fmtKg(nettoCalcolato) }}
                  components={{ strong: <strong /> }} />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.pesata_modal.netto_manuale')}</label>
              <input type="number" step="0.01" className={inputCls} placeholder={t('conferimenti.pesata_modal.netto_placeholder')} {...register('peso_netto_kg')} />
            </div>
            {serverError && (
              <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">{serverError}</div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">{t('common.azioni.annulla')}</button>
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60">
              {isSubmitting ? t('conferimenti.pesata_modal.salvataggio') : t('conferimenti.pesata_modal.registra')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
